import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import styles from './RefreshButton.module.css';

interface Props {
  /** Query-key prefixes to invalidate, e.g. [['admin','shipments']] */
  queryKeys: unknown[][];
  title?: string;
  /** Show a text label next to the icon */
  label?: string;
}

/**
 * Refetches the queries behind a view/section. The icon spins while any
 * matching query is in flight.
 */
export function RefreshButton({ queryKeys, title = 'Refresh', label }: Props) {
  const queryClient = useQueryClient();

  const fetching = useIsFetching({
    predicate: (q) => queryKeys.some((key) => key.every((part, i) => q.queryKey[i] === part)),
  });

  return (
    <button
      type="button"
      className={styles.btn}
      title={title}
      aria-label={title}
      disabled={fetching > 0}
      onClick={() => queryKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }))}
    >
      <svg
        className={fetching > 0 ? `${styles.icon} ${styles.spinning}` : styles.icon}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
        <polyline points="21 3 21 9 15 9" />
      </svg>
      {label && <span className={styles.label}>{label}</span>}
    </button>
  );
}
